import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Check, ChevronRight, ChevronLeft, ChevronDown, Calendar, Users, Clock, ListChecks,
  ChefHat, Wine, Sparkles, X, Music2, ArrowRight, Camera, Plane, Cake, Plus, Trash2, MapPin, Flower2, Star, Filter
} from "lucide-react";
import { ITALIAN_REGIONS } from "@/pages/Artists";

const EVENT_TYPES = [
  { key: "matrimonio", label: "Matrimonio", icon: "💍" },
  { key: "addio_celibato", label: "Addio al celibato", icon: "🥃" },
  { key: "addio_nubilato", label: "Addio al nubilato", icon: "👯" },
  { key: "laurea", label: "Festa di laurea", icon: "🎓" },
  { key: "compleanno", label: "Compleanno", icon: "🎂" },
  { key: "battesimo", label: "Battesimo", icon: "👶" },
  { key: "cresima", label: "Cresima", icon: "✝️" },
  { key: "comunione", label: "Prima Comunione", icon: "🕊️" },
  { key: "fidanzamento", label: "Fidanzamento", icon: "💞" },
  { key: "cenone_capodanno", label: "Cenone di Capodanno", icon: "🎆" },
  { key: "pranzo_natale", label: "Pranzo di Natale", icon: "🎄" },
  { key: "pranzo_pasqua", label: "Pranzo di Pasqua", icon: "🐣" },
  { key: "pranzo_pasquetta", label: "Pranzo di Pasquetta", icon: "🧺" },
  { key: "pranzo_ferragosto", label: "Pranzo di Ferragosto", icon: "🏖️" },
  { key: "cena_ferragosto", label: "Cena di Ferragosto", icon: "🌅" },
  { key: "altro", label: "Altro", icon: "✨" },
];

const DRINK_CATEGORIES = [
  { key: "acqua",       label: "Acqua",              icon: "💧", price: 0.80,  per: 4 },
  { key: "vino_rosso",  label: "Vino rosso",         icon: "🍷", price: 8.00,  per: 4 },
  { key: "vino_bianco", label: "Vino bianco",        icon: "🥂", price: 8.00,  per: 5 },
  { key: "birra",       label: "Birra",              icon: "🍺", price: 1.50,  per: 6 },
  { key: "bibite",      label: "Bibite analcoliche", icon: "🥤", price: 1.20,  per: 4 },
  { key: "spumante",    label: "Spumante",           icon: "🍾", price: 6.00,  per: 6 },
  { key: "amari",       label: "Amari e digestivi",  icon: "🥃", price: 15.00, per: 12 },
  { key: "caffe",       label: "Caffè",              icon: "☕", price: 0.50,  per: 1 },
  { key: "champagne",   label: "Champagne",          icon: "🍾", price: 40.00, per: 10 },
];

const CAT_LABELS = {
  mangiafuoco: "🔥 Mangiafuoco / Fire show",
  giocoliere: "🤹 Giocolieri",
  clown: "🤡 Clown / Mimi / Comicità",
  trapezzista: "🎪 Acrobati / Trapezzisti",
  trampolieri: "🦒 Trampolieri",
  circus: "🎭 Circo / Performance",
  mago: "🎩 Maghi / Prestigiatori",
  spogliarellista: "💃 Burlesque",
  fotografo: "📸 Fotografi",
  dronista: "🚁 Dronisti",
  band: "🎸 Band / Orchestra",
  musicista: "🎷 Musicisti (Sax, Violino...)",
  street_artist: "🎤 Artisti di strada / Buskers",
  animatore: "🎈 Animatori / Intrattenitori",
  dj: "🎧 DJ",
};

function groupArtistsByCategory(artists) {
  const grouped = {};
  for (const a of artists) {
    if (!grouped[a.category]) grouped[a.category] = [];
    grouped[a.category].push(a);
  }
  return grouped;
}

const STEPS = [
  { key: "type", label: "Tipo evento", icon: Sparkles },
  { key: "details", label: "Dettagli", icon: Calendar },
  { key: "timeline", label: "Programma", icon: Clock },
  { key: "services", label: "Servizi", icon: Music2 },
  { key: "decorations", label: "Addobbi", icon: Flower2 },
  { key: "menu", label: "Menù", icon: ChefHat },
  { key: "drinks", label: "Bevande", icon: Wine },
  { key: "confirm", label: "Conferma", icon: Check },
];

const DECORATION_PRESETS = [
  { name: "Centrotavola floreale", price: 35, icon: "🌸" },
  { name: "Bouquet sposa", price: 120, icon: "💐" },
  { name: "Bouquet damigelle", price: 60, icon: "💐" },
  { name: "Arco floreale cerimonia", price: 350, icon: "🌿" },
  { name: "Allestimento sala (drappeggi)", price: 450, icon: "✨" },
  { name: "Composizioni segnaposto", price: 4, icon: "🏷️" },
  { name: "Candele e portacandele", price: 8, icon: "🕯️" },
  { name: "Allestimento ingresso", price: 200, icon: "🚪" },
  { name: "Allestimento tavolo torta", price: 150, icon: "🎂" },
  { name: "Tableau de mariage", price: 180, icon: "📋" },
  { name: "Allestimento auto", price: 100, icon: "🚗" },
  { name: "Petali per uscita", price: 50, icon: "🌹" },
];

export default function EventWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [eventId, setEventId] = useState(id || null);
  const [dishes, setDishes] = useState([]);
  const [artists, setArtists] = useState([]);
  const [favArtistIds, setFavArtistIds] = useState(new Set());
  const [form, setForm] = useState({
    client_name: "",
    event_type: "",
    guests: 50,
    event_date: "",
    start_time: "19:00",
    end_time: "00:00",
    service_style: "servito",
    timeline: [],
    dishes: { antipasto: [], primo: [], secondo: [], contorno: [], dolce: [], pizza: [] },
    drinks: [],
    drinks_included: true,
    drinks_config: Object.fromEntries(DRINK_CATEGORIES.map((d) => [d.key, { mode: "none", qty: 1, per_people: d.per, price: d.price }])),
    photographer_id: null,  // legacy
    drone_id: null,  // legacy
    photographers_bookings: [],
    drones_bookings: [],
    artists_bookings: [],
    cake: { enabled: false, persons: 0, tier: "base", price: 250 },
    location: { name: "", address: "", capacity: 0, external: false, price: 0 },
    decorations: { enabled: false, theme: "", items: [], notes: "" },
    notes: "",
  });

  useEffect(() => {
    Promise.all([api.get("/dishes"), api.get("/artists"), api.get("/auth/me")]).then(([d, a, u]) => {
      setDishes(d.data); setArtists(a.data);
      // ensure cake default prices come from user settings
      if (u?.data?.cake_prices) {
        setForm((f) => ({
          ...f,
          cake: { ...f.cake, price: f.cake.price || u.data.cake_prices[f.cake.tier] || 250 },
          _cake_prices: u.data.cake_prices,
        }));
      }
    });
    api.get("/favorites/artist").then((r) => setFavArtistIds(new Set(r.data))).catch(() => {});
    if (id) {
      api.get(`/events/${id}`).then((r) => {
        const defaultDrinks = Object.fromEntries(DRINK_CATEGORIES.map((d) => [d.key, { mode: "none", qty: 1, per_people: d.per, price: d.price }]));
        setForm((f) => ({
          ...f,
          ...r.data,
          timeline: r.data.timeline || [],
          service_style: r.data.service_style || "servito",
          drinks_config: { ...defaultDrinks, ...(r.data.drinks_config || {}) },
          cake: r.data.cake && Object.keys(r.data.cake).length ? r.data.cake : { enabled: false, persons: 0, tier: "base", price: 250 },
          location: r.data.location && Object.keys(r.data.location).length ? r.data.location : { name: "", address: "", capacity: 0, external: false, price: 0 },
          decorations: r.data.decorations && Object.keys(r.data.decorations).length ? r.data.decorations : { enabled: false, theme: "", items: [], notes: "" },
          artists_bookings: (r.data.artists_bookings || []).map((b) => ({
            // migrate legacy {start_time, end_time} to {arrival_time, hours}
            arrival_time: b.arrival_time || b.start_time || "",
            hours: b.hours ?? "",
            ...b,
          })),
          photographer_booking: r.data.photographer_booking || {},
          drone_booking: r.data.drone_booking || {},
          photographers_bookings: r.data.photographers_bookings && r.data.photographers_bookings.length
            ? r.data.photographers_bookings
            : (r.data.photographer_id ? [{ artist_id: r.data.photographer_id, ...(r.data.photographer_booking || {}) }] : []),
          drones_bookings: r.data.drones_bookings && r.data.drones_bookings.length
            ? r.data.drones_bookings
            : (r.data.drone_id ? [{ artist_id: r.data.drone_id, ...(r.data.drone_booking || {}) }] : []),
        }));
      });
    }
  }, [id]);

  const next = async () => {
    if (step === 0 && !form.event_type) return toast.error("Seleziona un tipo di evento");
    if (step === 1 && (!form.guests || !form.event_date)) return toast.error("Inserisci ospiti e data");
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const saveDraft = async () => {
    try {
      if (eventId) {
        await api.put(`/events/${eventId}`, form);
      } else {
        const r = await api.post("/events", form);
        setEventId(r.data.id);
        window.history.replaceState({}, "", `/eventi/${r.data.id}/modifica`);
      }
      toast.success("Bozza salvata");
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(e.response.data?.detail || "Quota esaurita");
        navigate("/impostazioni");
      } else {
        toast.error("Errore nel salvataggio");
      }
    }
  };

  const finish = async () => {
    try {
      let eid = eventId;
      if (!eid) {
        const r = await api.post("/events", form);
        eid = r.data.id;
      } else {
        await api.put(`/events/${eid}`, form);
      }
      toast.loading("Calcolo costi in corso...", { id: "calc" });
      await api.post(`/events/${eid}/calculate`);
      toast.success("Evento creato!", { id: "calc" });
      navigate(`/eventi/${eid}`);
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(e.response.data?.detail || "Quota gratuita esaurita. Sottoscrivi un piano.", { id: "calc" });
        navigate("/impostazioni");
      } else {
        toast.error("Errore: " + (e?.response?.data?.detail || e.message), { id: "calc" });
      }
    }
  };

  return (
    <div className="space-y-8" data-testid="event-wizard">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-[#2563EB] font-semibold font-ui">Pianificazione evento</p>
        <h1 className="font-display text-4xl md:text-5xl text-[#0F2741] tracking-tight mt-2">{eventId ? "Modifica evento" : "Nuovo evento"}</h1>
      </div>

      {/* Stepper */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 overflow-x-auto">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < step, active = i === step;
            return (
              <div key={s.key} className="flex items-center gap-2 shrink-0">
                <button onClick={() => setStep(i)} data-testid={`step-${s.key}`} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${active ? "bg-[#0F2741] text-white" : done ? "text-[#2563EB]" : "text-slate-400"}`}>
                  <div className={`w-6 h-6 grid place-items-center rounded-full ${active ? "bg-[#2563EB]" : done ? "bg-[#2563EB] text-white" : "bg-slate-100"}`}>
                    {done ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                  </div>
                  <span className="hidden md:inline">{s.label}</span>
                </button>
                {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 md:p-10">
        {step === 0 && <StepType form={form} setForm={setForm} />}
        {step === 1 && <StepDetails form={form} setForm={setForm} />}
        {step === 2 && <StepTimeline form={form} setForm={setForm} />}
        {step === 3 && <StepServices form={form} setForm={setForm} artists={artists} favArtistIds={favArtistIds} />}
        {step === 4 && <StepDecorations form={form} setForm={setForm} />}
        {step === 5 && <StepMenu form={form} setForm={setForm} dishes={dishes} />}
        {step === 6 && <StepDrinks form={form} setForm={setForm} />}
        {step === 7 && <StepConfirm form={form} dishes={dishes} artists={artists} />}
      </div>

      <div className="flex justify-between gap-2">
        <button onClick={prev} disabled={step === 0} className="btn-ghost"><ChevronLeft className="w-4 h-4" /> Indietro</button>
        <div className="flex gap-2">
          <button onClick={saveDraft} className="btn-ghost border border-slate-200">Salva bozza</button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} data-testid="btn-wizard-next" className="btn-primary">
              {step === 2 ? <>HO FINITO <Check className="w-4 h-4" /></> : <>Avanti <ChevronRight className="w-4 h-4" /></>}
            </button>
          ) : (
            <button onClick={finish} data-testid="btn-wizard-finish" className="btn-primary">Conferma e calcola <ArrowRight className="w-4 h-4" /></button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepType({ form, setForm }) {
  return (
    <div>
      <h2 className="font-display text-2xl text-[#0F2741] mb-2">Che tipo di evento celebriamo?</h2>
      <p className="text-slate-500 mb-8">Scegli la tipologia per personalizzare ogni dettaglio.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {EVENT_TYPES.map((t) => (
          <button key={t.key} onClick={() => setForm({ ...form, event_type: t.key })} data-testid={`type-${t.key}`}
            className={`p-4 rounded-xl border-2 transition-all text-left ${form.event_type === t.key ? "border-[#2563EB] bg-[#EFF6FF]" : "border-slate-200 hover:border-[#2563EB]/50"}`}>
            <div className="text-2xl">{t.icon}</div>
            <p className="mt-2 font-ui font-medium text-sm text-[#0F2741]">{t.label}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepDetails({ form, setForm }) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-2xl text-[#0F2741] mb-2">Dettagli dell'evento</h2>
        <p className="text-slate-500 mb-8">Cliente, ospiti e orario.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Nome cliente / contatto">
            <input data-testid="input-client-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="input" placeholder="Es. Famiglia Rossi" />
          </Field>
          <Field label="Numero ospiti" icon={Users}>
            <input data-testid="input-guests" type="number" min="1" value={form.guests} onChange={(e) => setForm({ ...form, guests: parseInt(e.target.value) || 0 })} className="input" />
          </Field>
          <Field label="Data evento" icon={Calendar}>
            <input data-testid="input-date" type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="input" />
          </Field>
          <Field label="Ora inizio" icon={Clock}>
            <input data-testid="input-start" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="input" />
          </Field>
          <Field label="Ora fine" icon={Clock}>
            <input data-testid="input-end" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="input" />
          </Field>
          <Field label="Note">
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" placeholder="Allergie, preferenze, richieste speciali..." />
          </Field>
        </div>

        {/* Service Style */}
        <div className="mt-8" data-testid="section-service-style">
          <div className="flex items-center gap-2 mb-2">
            <ChefHat className="w-4 h-4 text-[#2563EB]" />
            <h3 className="font-display text-xl text-[#0F2741]">Stile di servizio</h3>
          </div>
          <p className="text-slate-500 mb-4 text-sm">Definisce come verrà servito il pasto principale. Influisce sul numero di camerieri e sul tipo di portate.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { key: "servito", label: "Servizio al tavolo", desc: "Servizio classico, 1 cameriere ogni 10 ospiti", icon: "🍽️" },
              { key: "buffet", label: "Buffet", desc: "Ospiti si servono, 1 cameriere ogni 20 ospiti", icon: "🥗" },
              { key: "misto", label: "Misto", desc: "Alcuni momenti serviti, altri a buffet", icon: "✨" },
              { key: "solo_aperitivo", label: "Solo aperitivo", desc: "Drink + finger food, 1 cameriere ogni 15 ospiti", icon: "🥂" },
              { key: "solo_rinfresco", label: "Solo rinfresco", desc: "Buffet leggero post-cerimonia, 1 cameriere ogni 18 ospiti", icon: "🍰" },
            ].map((s) => (
              <button
                type="button"
                key={s.key}
                onClick={() => setForm({ ...form, service_style: s.key })}
                data-testid={`service-style-${s.key}`}
                className={`text-left p-4 rounded-xl border-2 transition-colors ${form.service_style === s.key ? "border-[#2563EB] bg-[#EFF6FF]" : "border-slate-200 hover:border-[#2563EB]/50"}`}
              >
                <p className="text-2xl mb-1">{s.icon}</p>
                <p className="font-ui font-semibold text-[#0F2741]">{s.label}</p>
                <p className="text-xs text-slate-500 mt-1">{s.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Location */}
      <div className="border-t border-slate-200 pt-8" data-testid="section-location">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-4 h-4 text-[#2563EB]" />
          <h3 className="font-display text-xl text-[#0F2741]">Location</h3>
        </div>
        <p className="text-slate-500 mb-5">Dove si svolgerà l'evento. Se la location è esterna al ristorante, indica il costo per addebitarlo al cliente.</p>

        <div className="flex gap-3 mb-5">
          <button type="button" onClick={() => setForm({ ...form, location: { ...form.location, external: false, price: 0 } })}
            className={`flex-1 p-3 rounded-xl border-2 text-left ${!form.location?.external ? "border-[#2563EB] bg-[#EFF6FF]" : "border-slate-200"}`}>
            <p className="font-ui font-semibold text-[#0F2741] text-sm">Location interna</p>
            <p className="text-xs text-slate-500 mt-0.5">Il ristorante / sala eventi propria</p>
          </button>
          <button type="button" onClick={() => setForm({ ...form, location: { ...form.location, external: true } })}
            className={`flex-1 p-3 rounded-xl border-2 text-left ${form.location?.external ? "border-[#2563EB] bg-[#EFF6FF]" : "border-slate-200"}`}>
            <p className="font-ui font-semibold text-[#0F2741] text-sm">Location esterna</p>
            <p className="text-xs text-slate-500 mt-0.5">Villa, agriturismo, location esterna (con costo)</p>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Nome location">
            <input data-testid="input-location-name" value={form.location?.name || ""} onChange={(e) => setForm({ ...form, location: { ...form.location, name: e.target.value } })} className="input" placeholder="Es. Villa Borghese" />
          </Field>
          <Field label="Indirizzo">
            <input value={form.location?.address || ""} onChange={(e) => setForm({ ...form, location: { ...form.location, address: e.target.value } })} className="input" placeholder="Via, città, CAP" />
          </Field>
          <Field label="Capienza massima">
            <input type="number" min="0" value={form.location?.capacity || ""} onChange={(e) => setForm({ ...form, location: { ...form.location, capacity: parseInt(e.target.value) || 0 } })} className="input" placeholder="Es. 200" />
          </Field>
          {form.location?.external && (
            <Field label="Costo location esterna (€)">
              <input type="number" step="50" min="0" data-testid="input-location-price" value={form.location?.price || ""} onChange={(e) => setForm({ ...form, location: { ...form.location, price: parseFloat(e.target.value) || 0 } })} className="input font-semibold" placeholder="Es. 2000" />
            </Field>
          )}
        </div>
        {form.location?.external && form.location?.capacity > 0 && form.guests > form.location.capacity && (
          <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            ⚠️ La location ha capienza {form.location.capacity}, ma sono previsti {form.guests} ospiti.
          </p>
        )}
      </div>

      <style>{`.input{width:100%;padding:0.625rem 0.875rem;border-radius:0.5rem;border:1px solid #E2E8F0;outline:none;font-size:0.875rem;} .input:focus{ring:2px solid #2563EB;border-color:#2563EB;box-shadow:0 0 0 3px rgba(37,99,235,.2)}`}</style>
    </div>
  );
}

function StepDecorations({ form, setForm }) {
  const dec = form.decorations || { enabled: false, theme: "", items: [], notes: "" };
  const setDec = (patch) => setForm({ ...form, decorations: { ...dec, ...patch } });

  const addPreset = (preset) => {
    const exists = dec.items.find((it) => it.name === preset.name);
    if (exists) return;
    setDec({ enabled: true, items: [...dec.items, { name: preset.name, qty: 1, price: preset.price, icon: preset.icon }] });
  };

  const addCustom = () => {
    setDec({ enabled: true, items: [...dec.items, { name: "", qty: 1, price: 0, icon: "✨" }] });
  };

  const updateItem = (idx, patch) => {
    const items = [...dec.items];
    items[idx] = { ...items[idx], ...patch };
    setDec({ items });
  };
  const removeItem = (idx) => setDec({ items: dec.items.filter((_, i) => i !== idx) });

  const total = dec.items.reduce((s, it) => s + (parseFloat(it.qty) || 1) * (parseFloat(it.price) || 0), 0);

  return (
    <div className="space-y-8" data-testid="step-decorations">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Flower2 className="w-5 h-5 text-[#2563EB]" />
          <h2 className="font-display text-2xl text-[#0F2741]">Addobbi & Fiori</h2>
        </div>
        <p className="text-slate-500">Aggiungi composizioni floreali e allestimenti. Quantità e prezzi sono sempre modificabili.</p>
      </div>

      <div className="flex items-center gap-4">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!dec.enabled} onChange={(e) => setDec({ enabled: e.target.checked })} data-testid="toggle-decorations" className="accent-[#2563EB]" />
          <span className="text-sm text-slate-700 font-semibold">Includi addobbi e fiori nell'evento</span>
        </label>
      </div>

      {dec.enabled && (
        <>
          <Field label="Tema / stile addobbi">
            <input value={dec.theme} onChange={(e) => setDec({ theme: e.target.value })} placeholder="Es. Romantico bianco e oro, Boho chic, Vintage..." className="w-full px-3 py-2 rounded-lg border border-slate-200" />
          </Field>

          <div>
            <h3 className="font-ui font-semibold text-[#0F2741] mb-3">Preset addobbi</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {DECORATION_PRESETS.map((p) => {
                const added = dec.items.find((it) => it.name === p.name);
                return (
                  <button key={p.name} type="button" onClick={() => addPreset(p)} disabled={!!added} data-testid={`preset-${p.name}`}
                    className={`text-left p-3 rounded-lg border transition-all ${added ? "border-[#2563EB] bg-[#EFF6FF] opacity-60 cursor-default" : "border-slate-200 hover:border-[#2563EB]/50 bg-white"}`}>
                    <div className="text-xl">{p.icon}</div>
                    <p className="text-xs font-semibold text-[#0F2741] mt-1 leading-tight">{p.name}</p>
                    <p className="text-xs text-[#1D4ED8] mt-1">€{p.price}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-ui font-semibold text-[#0F2741]">Voci aggiunte ({dec.items.length})</h3>
              <button type="button" onClick={addCustom} className="btn-ghost border border-slate-200"><Plus className="w-4 h-4" /> Voce custom</button>
            </div>

            {dec.items.length === 0 ? (
              <div className="text-sm text-slate-500 p-6 rounded-xl border border-dashed border-slate-300 text-center">
                Nessuna voce aggiunta. Scegli un preset oppure aggiungi una voce custom.
              </div>
            ) : (
              <div className="space-y-2">
                {dec.items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 rounded-lg border border-slate-200 bg-white">
                    <div className="col-span-1 text-2xl text-center">{it.icon || "✨"}</div>
                    <div className="col-span-5">
                      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Voce</label>
                      <input value={it.name} onChange={(e) => updateItem(idx, { name: e.target.value })} placeholder="Nome addobbo" className="mt-1 w-full px-2 py-1.5 rounded border border-slate-200 text-sm" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Quantità</label>
                      <input type="number" min="1" step="1" value={it.qty} onChange={(e) => updateItem(idx, { qty: parseInt(e.target.value) || 1 })} className="mt-1 w-full px-2 py-1.5 rounded border border-slate-200 text-sm text-right" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Prezzo unitario (€)</label>
                      <input type="number" min="0" step="1" value={it.price} onChange={(e) => updateItem(idx, { price: parseFloat(e.target.value) || 0 })} className="mt-1 w-full px-2 py-1.5 rounded border border-slate-200 text-sm text-right" />
                    </div>
                    <div className="col-span-1 text-right text-sm font-semibold text-[#0F2741]">€{((it.qty || 1) * (it.price || 0)).toFixed(0)}</div>
                    <div className="col-span-1 text-right">
                      <button type="button" onClick={() => removeItem(idx)} className="text-rose-400 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
                <div className="p-4 rounded-lg bg-gradient-to-r from-[#EFF6FF] to-white border border-[#2563EB]/20 flex justify-between items-center">
                  <span className="font-ui font-semibold text-[#0F2741]">Totale addobbi & fiori</span>
                  <span className="font-display text-2xl text-[#2563EB]">€ {total.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          <Field label="Note allestimento">
            <textarea rows={3} value={dec.notes} onChange={(e) => setDec({ notes: e.target.value })} placeholder="Indicazioni per il fiorista, allergie, preferenze..." className="w-full px-3 py-2 rounded-lg border border-slate-200" />
          </Field>
        </>
      )}
    </div>
  );
}

function Field({ label, children, icon: Icon }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5 mb-1.5">
        {Icon && <Icon className="w-3 h-3" />}{label}
      </span>
      {children}
    </label>
  );
}

const TIMELINE_OPTIONS = [
  { key: "accoglienza", label: "Accoglienza" },
  { key: "benvenuto", label: "Benvenuto" },
  { key: "calice_benvenuto", label: "Calice di benvenuto" },
  { key: "aperitivo", label: "Aperitivo" },
  { key: "antipasto", label: "Antipasto" },
  { key: "primi", label: "Primi" },
  { key: "secondi", label: "Secondi" },
  { key: "contorno", label: "Contorni" },
  { key: "dolci", label: "Dolci" },
  { key: "torta", label: "Torta" },
  { key: "caffe", label: "Caffè / Digestivi" },
  { key: "evento", label: "Momento evento (taglio torta, lancio bouquet…)" },
  { key: "artisti", label: "Artisti / Spettacolo" },
  { key: "musica", label: "Musica / DJ Set" },
  { key: "altro", label: "Altro (specifica nelle note)" },
];

const SERVICE_MODE_LABEL = { tavolo: "🍽️ Al tavolo", buffet: "🥗 A buffet", piedi: "🥂 In piedi" };

function StepTimeline({ form, setForm }) {
  const [draft, setDraft] = useState({ key: "", label: "", start_time: "", end_time: "", service_mode: "", notes: "" });

  const confirmRow = () => {
    if (!draft.key) {
      toast.error("Seleziona una voce dal menù");
      return;
    }
    const found = TIMELINE_OPTIONS.find((o) => o.key === draft.key);
    const newRow = {
      key: `${draft.key}_${Date.now()}`,
      label: draft.label || found?.label || draft.key,
      start_time: draft.start_time,
      end_time: draft.end_time,
      service_mode: draft.service_mode,
      notes: draft.notes,
      skip: false,
    };
    setForm({ ...form, timeline: [...form.timeline, newRow] });
    setDraft({ key: "", label: "", start_time: "", end_time: "", service_mode: "", notes: "" });
  };

  const removeRow = (idx) => {
    const t = form.timeline.filter((_, i) => i !== idx);
    setForm({ ...form, timeline: t });
  };

  const updateRow = (idx, patch) => {
    const t = form.timeline.map((r, i) => i === idx ? { ...r, ...patch } : r);
    setForm({ ...form, timeline: t });
  };

  return (
    <div>
      <h2 className="font-display text-2xl text-[#0F2741] mb-2">Programma dell'evento</h2>
      <p className="text-slate-500 mb-6">Costruisci il programma riga per riga: scegli la voce, l'orario, la modalità di servizio (tavolo / buffet / in piedi) e aggiungi eventuali note. Premi <strong>Conferma riga</strong> per aggiungerla.</p>

      {/* Existing rows */}
      {form.timeline.length > 0 && (
        <div className="space-y-2 mb-6" data-testid="timeline-rows">
          {form.timeline.map((s, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center p-3 rounded-lg border-2 border-[#2563EB]/20 bg-[#EFF6FF]/30">
              <div className="md:col-span-3">
                <p className="font-ui font-semibold text-[#0F2741] text-sm" data-testid={`row-${i}-label`}>{s.label}</p>
                {s.service_mode && <p className="text-xs text-slate-500 mt-0.5">{SERVICE_MODE_LABEL[s.service_mode] || s.service_mode}</p>}
              </div>
              <div className="md:col-span-2 flex items-center gap-1 text-sm">
                <Clock className="w-3 h-3 text-slate-400" />
                <span className="text-slate-700">{s.start_time || "—"}<span className="text-slate-400 mx-1">→</span>{s.end_time || "—"}</span>
              </div>
              <div className="md:col-span-6">
                {s.notes ? <p className="text-xs text-slate-600 italic">"{s.notes}"</p> : <p className="text-xs text-slate-400">Nessuna nota</p>}
              </div>
              <div className="md:col-span-1 flex justify-end">
                <button type="button" onClick={() => removeRow(i)} data-testid={`btn-remove-row-${i}`} className="text-rose-400 hover:text-rose-600 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Draft row */}
      <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-4" data-testid="timeline-draft-row">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">{form.timeline.length === 0 ? "Aggiungi la prima voce" : "Aggiungi un'altra voce"}</p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
          <div className="md:col-span-4">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Voce</label>
            <select
              data-testid="draft-key"
              value={draft.key}
              onChange={(e) => {
                const opt = TIMELINE_OPTIONS.find((o) => o.key === e.target.value);
                setDraft({ ...draft, key: e.target.value, label: opt?.label || "" });
              }}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
            >
              <option value="">— Seleziona —</option>
              {TIMELINE_OPTIONS.map((o) => (<option key={o.key} value={o.key}>{o.label}</option>))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Da</label>
            <input data-testid="draft-start" type="time" value={draft.start_time} onChange={(e) => setDraft({ ...draft, start_time: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">A</label>
            <input data-testid="draft-end" type="time" value={draft.end_time} onChange={(e) => setDraft({ ...draft, end_time: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </div>
          <div className="md:col-span-4">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Modalità</label>
            <div className="flex gap-1 mt-1">
              {[{ k: "tavolo", l: "🍽️ Tavolo" }, { k: "buffet", l: "🥗 Buffet" }, { k: "piedi", l: "🥂 In piedi" }].map((m) => (
                <button
                  type="button"
                  key={m.k}
                  onClick={() => setDraft({ ...draft, service_mode: draft.service_mode === m.k ? "" : m.k })}
                  data-testid={`draft-mode-${m.k}`}
                  className={`flex-1 px-2 py-2 rounded-lg text-xs font-medium border transition-colors ${draft.service_mode === m.k ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white text-slate-600 border-slate-200 hover:border-[#2563EB]"}`}
                >{m.l}</button>
              ))}
            </div>
          </div>
          <div className="md:col-span-9">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Note</label>
            <input
              data-testid="draft-notes"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder="Es. Aperitivo all'aperto, antipasto vegetariano, brindisi sposi..."
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
            />
          </div>
          <div className="md:col-span-3 flex md:items-end md:justify-end">
            <button
              type="button"
              onClick={confirmRow}
              disabled={!draft.key}
              data-testid="btn-confirm-row"
              className="w-full md:w-auto btn-primary disabled:opacity-50 mt-0 md:mt-5"
            >
              <Check className="w-4 h-4" /> Conferma riga
            </button>
          </div>
        </div>
      </div>

      {form.timeline.length === 0 && (
        <p className="mt-4 text-xs text-slate-400 italic text-center">Nessuna voce nel programma. Aggiungi la prima qui sopra.</p>
      )}
    </div>
  );
}

function StepServices({ form, setForm, artists, favArtistIds }) {
  const photographers = artists.filter((a) => a.category === "fotografo");
  const drones = artists.filter((a) => a.category === "dronista");
  // Artists pickable here = everything except photographer/dronista (which have their own slots)
  const stageArtists = artists.filter((a) => !["fotografo", "dronista"].includes(a.category));
  const cakePrices = form._cake_prices || { base: 250, superior: 500, luxury: 1000 };

  // Show favorites first; if user has none in that bucket, fall back to "all".
  const favPhotogs = photographers.filter((a) => favArtistIds.has(a.id));
  const favDrones = drones.filter((a) => favArtistIds.has(a.id));
  const favStage = stageArtists.filter((a) => favArtistIds.has(a.id));

  const [photogShowAll, setPhotogShowAll] = useState(favPhotogs.length === 0);
  const [droneShowAll, setDroneShowAll] = useState(favDrones.length === 0);
  const [stageShowAll, setStageShowAll] = useState(favStage.length === 0);

  // Accordion state — all sections collapsed by default
  const [openSecs, setOpenSecs] = useState({ photog: false, drone: false, artists: false, cake: false });
  const toggleSec = (key) => setOpenSecs((s) => ({ ...s, [key]: !s[key] }));

  // Inline filters when "show all" is on
  const [pFilter, setPFilter] = useState({ region: "all", city: "", max: "" });
  const [dFilter, setDFilter] = useState({ region: "all", city: "", max: "" });
  const [sFilter, setSFilter] = useState({ region: "all", city: "", max: "", category: "all" });

  const applyFilter = (list, f) => list.filter((a) => {
    if (f.region !== "all" && (a.region || "").toLowerCase() !== f.region.toLowerCase()) return false;
    if (f.city && !(a.city || "").toLowerCase().includes(f.city.toLowerCase())) return false;
    if (f.max && Number(a.price_eur) > parseFloat(f.max)) return false;
    if (f.category && f.category !== "all" && a.category !== f.category) return false;
    return true;
  });

  const visiblePhotogs = photogShowAll ? applyFilter(photographers, pFilter) : favPhotogs;
  const visibleDrones = droneShowAll ? applyFilter(drones, dFilter) : favDrones;
  const stagePool = stageShowAll ? applyFilter(stageArtists, sFilter) : favStage;
  const grouped = groupArtistsByCategory(stagePool);

  const togglePhotographer = (id) => {
    const list = form.photographers_bookings || [];
    const exists = list.find((b) => b.artist_id === id);
    if (exists) {
      setForm({ ...form, photographers_bookings: list.filter((b) => b.artist_id !== id) });
    } else {
      const a = artists.find((x) => x.id === id);
      setForm({
        ...form,
        photographers_bookings: [...list, { artist_id: id, arrival_time: "", hours: "", custom_price: a?.price_eur ?? "" }],
      });
    }
  };
  const toggleDrone = (id) => {
    const list = form.drones_bookings || [];
    const exists = list.find((b) => b.artist_id === id);
    if (exists) {
      setForm({ ...form, drones_bookings: list.filter((b) => b.artist_id !== id) });
    } else {
      const a = artists.find((x) => x.id === id);
      setForm({
        ...form,
        drones_bookings: [...list, { artist_id: id, arrival_time: "", hours: "", custom_price: a?.price_eur ?? "" }],
      });
    }
  };
  const updatePhotogBookingAt = (artistId, patch) => {
    const list = (form.photographers_bookings || []).map((b) => b.artist_id === artistId ? { ...b, ...patch } : b);
    setForm({ ...form, photographers_bookings: list });
  };
  const updateDroneBookingAt = (artistId, patch) => {
    const list = (form.drones_bookings || []).map((b) => b.artist_id === artistId ? { ...b, ...patch } : b);
    setForm({ ...form, drones_bookings: list });
  };

  // Add empty slot helpers (open a panel with a select)
  const addEmptyPhotog = () => setForm({
    ...form,
    photographers_bookings: [...(form.photographers_bookings || []), { artist_id: "", arrival_time: "", hours: "", custom_price: "" }],
  });
  const addEmptyDrone = () => setForm({
    ...form,
    drones_bookings: [...(form.drones_bookings || []), { artist_id: "", arrival_time: "", hours: "", custom_price: "" }],
  });
  const replacePhotogAt = (idx, artistId) => {
    const a = artists.find((x) => x.id === artistId);
    const list = [...(form.photographers_bookings || [])];
    list[idx] = { ...list[idx], artist_id: artistId, custom_price: a?.price_eur ?? "" };
    setForm({ ...form, photographers_bookings: list });
  };
  const replaceDroneAt = (idx, artistId) => {
    const a = artists.find((x) => x.id === artistId);
    const list = [...(form.drones_bookings || [])];
    list[idx] = { ...list[idx], artist_id: artistId, custom_price: a?.price_eur ?? "" };
    setForm({ ...form, drones_bookings: list });
  };
  const updatePhotogAt = (idx, patch) => {
    const list = [...(form.photographers_bookings || [])];
    list[idx] = { ...list[idx], ...patch };
    setForm({ ...form, photographers_bookings: list });
  };
  const updateDroneAt = (idx, patch) => {
    const list = [...(form.drones_bookings || [])];
    list[idx] = { ...list[idx], ...patch };
    setForm({ ...form, drones_bookings: list });
  };
  const removePhotogAt = (idx) => setForm({
    ...form,
    photographers_bookings: (form.photographers_bookings || []).filter((_, i) => i !== idx),
  });
  const removeDroneAt = (idx) => setForm({
    ...form,
    drones_bookings: (form.drones_bookings || []).filter((_, i) => i !== idx),
  });

  const addBooking = () => {
    setForm({
      ...form,
      artists_bookings: [...(form.artists_bookings || []), { artist_id: "", arrival_time: "", hours: "", custom_price: null }],
    });
  };
  const addBookingFor = (artist) => {
    const already = (form.artists_bookings || []).some((b) => b.artist_id === artist.id);
    if (already) {
      toast.info(`${artist.name} è già stato aggiunto`);
      return;
    }
    setForm({
      ...form,
      artists_bookings: [...(form.artists_bookings || []), { artist_id: artist.id, arrival_time: "", hours: "", custom_price: artist.price_eur ?? null }],
    });
    toast.success(`${artist.name} aggiunto`);
  };
  const updateBooking = (idx, patch) => {
    const list = [...(form.artists_bookings || [])];
    list[idx] = { ...list[idx], ...patch };
    setForm({ ...form, artists_bookings: list });
  };
  const removeBooking = (idx) => {
    setForm({ ...form, artists_bookings: (form.artists_bookings || []).filter((_, i) => i !== idx) });
  };

  const setCakeTier = (tier) => {
    setForm({ ...form, cake: { ...form.cake, enabled: true, tier, price: cakePrices[tier] || form.cake.price } });
  };

  return (
    <div className="space-y-3" data-testid="services-step-wrapper">
      <p className="text-sm text-slate-500">Aggiungi i servizi che vuoi includere nell'evento. Clicca su una voce per aprirla.</p>
      <div>
        <h2 className="font-display text-2xl text-[#0F2741] mb-2">Servizi & Artisti</h2>
        <p className="text-slate-500">Aggiungi fotografo, dronista, artisti per lo spettacolo e la torta.</p>
      </div>

      {/* Photographer */}
      <section data-testid="services-photographer" className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <button type="button" onClick={() => toggleSec("photog")} data-testid="accordion-header-photog"
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition">
          <Camera className="w-5 h-5 text-[#2563EB]" />
          <h3 className="font-ui font-semibold text-[#0F2741]">Fotografi</h3>
          {(form.photographers_bookings || []).length > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{(form.photographers_bookings || []).length} aggiunti</span>
          )}
          <ChevronDown className={`w-5 h-5 ml-auto text-slate-400 transition-transform ${openSecs.photog ? "rotate-180" : ""}`} />
        </button>
        {openSecs.photog && (<div className="p-4 border-t border-slate-100">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs text-slate-500">{favPhotogs.length} preferiti · {photographers.length} totali</span>
          <button type="button" onClick={() => setPhotogShowAll((v) => !v)} data-testid="btn-toggle-photog-all"
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${photogShowAll ? "bg-[#EFF6FF] text-[#1D4ED8] border-[#2563EB]/30" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            <Star className={`w-3 h-3 ${photogShowAll ? "" : "fill-amber-400 text-amber-500"}`} />
            {photogShowAll ? "Solo preferiti" : `Tutto il DB (${photographers.length})`}
          </button>
          <button type="button" onClick={addEmptyPhotog} data-testid="btn-add-photog-booking" className="btn-primary ml-auto">
            <Plus className="w-4 h-4" /> Aggiungi fotografo
          </button>
        </div>
        {photogShowAll && (
          <div className="mb-3 p-3 rounded-lg border border-slate-200 bg-slate-50 flex flex-wrap gap-2">
            <Filter className="w-4 h-4 text-slate-400 self-center" />
            <select value={pFilter.region} onChange={(e) => setPFilter({ ...pFilter, region: e.target.value })} data-testid="photog-filter-region" className="px-2 py-1.5 rounded border border-slate-200 text-xs bg-white">
              <option value="all">Tutte regioni</option>
              {ITALIAN_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input value={pFilter.city} onChange={(e) => setPFilter({ ...pFilter, city: e.target.value })} placeholder="Città" data-testid="photog-filter-city" className="px-2 py-1.5 rounded border border-slate-200 text-xs" />
            <input type="number" min="0" step="100" value={pFilter.max} onChange={(e) => setPFilter({ ...pFilter, max: e.target.value })} placeholder="€ max" data-testid="photog-filter-max" className="px-2 py-1.5 rounded border border-slate-200 text-xs w-24" />
            <span className="text-xs text-slate-500 self-center ml-auto">{visiblePhotogs.length} risultati</span>
          </div>
        )}
        {(form.photographers_bookings || []).length > 0 && (
          <div className="mb-3 space-y-3">
            {(form.photographers_bookings || []).map((pb, idx) => {
              const ent = artists.find((x) => x.id === pb.artist_id);
              if (ent && pb.artist_id) {
                return (
                  <BookingPanel
                    key={idx}
                    title={`Fotografo ${idx + 1} selezionato`}
                    entity={ent}
                    booking={pb}
                    onChange={(patch) => updatePhotogAt(idx, patch)}
                    onRemove={() => removePhotogAt(idx)}
                    testidPrefix={`photographer-booking-${idx}`}
                    icon={Camera}
                  />
                );
              }
              return (
                <div key={idx} className="p-4 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/40" data-testid={`photographer-booking-empty-${idx}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-11">
                      <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Seleziona un fotografo dal database</label>
                      <select value={pb.artist_id} onChange={(e) => replacePhotogAt(idx, e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
                        <option value="">— Seleziona fotografo —</option>
                        {photographers.map((x) => <option key={x.id} value={x.id}>{x.name} · €{x.price_eur}{x.city ? ` · ${x.city}` : ""}</option>)}
                      </select>
                    </div>
                    <div className="md:col-span-1 text-right">
                      <button type="button" onClick={() => removePhotogAt(idx)} className="text-rose-400 hover:text-rose-600 p-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {visiblePhotogs.length === 0 ? (
          <p className="text-sm text-slate-500">
            {photogShowAll
              ? "Nessun fotografo trovato con questi filtri. Allarga la ricerca."
              : 'Nessun fotografo nei preferiti. Clicca "Mostra tutto il DB" e salva i tuoi preferiti dalla pagina Artisti.'}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {visiblePhotogs.map((a) => {
              const sel = (form.photographers_bookings || []).some((b) => b.artist_id === a.id);
              return (
                <button key={a.id} type="button" onClick={() => togglePhotographer(a.id)} data-testid={`photographer-${a.id}`}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${sel ? "border-[#2563EB] bg-[#EFF6FF]" : "border-slate-200 hover:border-[#2563EB]/50 bg-white"}`}>
                  <div className="flex justify-between items-start">
                    <p className="font-ui font-semibold text-[#0F2741]">{a.name}</p>
                    <span className="text-sm font-bold text-[#1D4ED8]">€{a.price_eur}</span>
                  </div>
                  {a.specialty && <p className="text-xs text-[#1D4ED8] mt-0.5">{a.specialty}</p>}
                  {(a.city || a.region) && <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[a.city, a.region].filter(Boolean).join(" · ")}</p>}
                </button>
              );
            })}
          </div>
        )}
        </div>)}
      </section>

      {/* Drone */}
      <section data-testid="services-drone" className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <button type="button" onClick={() => toggleSec("drone")} data-testid="accordion-header-drone"
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition">
          <Plane className="w-5 h-5 text-[#2563EB]" />
          <h3 className="font-ui font-semibold text-[#0F2741]">Servizio drone</h3>
          {(form.drones_bookings || []).length > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{(form.drones_bookings || []).length} aggiunti</span>
          )}
          <ChevronDown className={`w-5 h-5 ml-auto text-slate-400 transition-transform ${openSecs.drone ? "rotate-180" : ""}`} />
        </button>
        {openSecs.drone && (<div className="p-4 border-t border-slate-100">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs text-slate-500">{favDrones.length} preferiti · {drones.length} totali</span>
          <button type="button" onClick={() => setDroneShowAll((v) => !v)} data-testid="btn-toggle-drone-all"
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${droneShowAll ? "bg-[#EFF6FF] text-[#1D4ED8] border-[#2563EB]/30" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            <Star className={`w-3 h-3 ${droneShowAll ? "" : "fill-amber-400 text-amber-500"}`} />
            {droneShowAll ? "Solo preferiti" : `Tutto il DB (${drones.length})`}
          </button>
          <button type="button" onClick={addEmptyDrone} data-testid="btn-add-drone-booking" className="btn-primary ml-auto">
            <Plus className="w-4 h-4" /> Aggiungi dronista
          </button>
        </div>
        {droneShowAll && (
          <div className="mb-3 p-3 rounded-lg border border-slate-200 bg-slate-50 flex flex-wrap gap-2">
            <Filter className="w-4 h-4 text-slate-400 self-center" />
            <select value={dFilter.region} onChange={(e) => setDFilter({ ...dFilter, region: e.target.value })} className="px-2 py-1.5 rounded border border-slate-200 text-xs bg-white">
              <option value="all">Tutte regioni</option>
              {ITALIAN_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input value={dFilter.city} onChange={(e) => setDFilter({ ...dFilter, city: e.target.value })} placeholder="Città" className="px-2 py-1.5 rounded border border-slate-200 text-xs" />
            <input type="number" min="0" step="100" value={dFilter.max} onChange={(e) => setDFilter({ ...dFilter, max: e.target.value })} placeholder="€ max" className="px-2 py-1.5 rounded border border-slate-200 text-xs w-24" />
            <span className="text-xs text-slate-500 self-center ml-auto">{visibleDrones.length} risultati</span>
          </div>
        )}
        {(form.drones_bookings || []).length > 0 && (
          <div className="mb-3 space-y-3">
            {(form.drones_bookings || []).map((db, idx) => {
              const ent = artists.find((x) => x.id === db.artist_id);
              if (ent && db.artist_id) {
                return (
                  <BookingPanel
                    key={idx}
                    title={`Drone ${idx + 1} selezionato`}
                    entity={ent}
                    booking={db}
                    onChange={(patch) => updateDroneAt(idx, patch)}
                    onRemove={() => removeDroneAt(idx)}
                    testidPrefix={`drone-booking-${idx}`}
                    icon={Plane}
                  />
                );
              }
              return (
                <div key={idx} className="p-4 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/40" data-testid={`drone-booking-empty-${idx}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-11">
                      <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Seleziona un dronista dal database</label>
                      <select value={db.artist_id} onChange={(e) => replaceDroneAt(idx, e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
                        <option value="">— Seleziona dronista —</option>
                        {drones.map((x) => <option key={x.id} value={x.id}>{x.name} · €{x.price_eur}{x.city ? ` · ${x.city}` : ""}</option>)}
                      </select>
                    </div>
                    <div className="md:col-span-1 text-right">
                      <button type="button" onClick={() => removeDroneAt(idx)} className="text-rose-400 hover:text-rose-600 p-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {visibleDrones.length === 0 ? (
          <p className="text-sm text-slate-500">
            {droneShowAll ? "Nessun dronista disponibile con questi filtri." : "Nessun dronista nei preferiti. Espandi il DB per sceglierne uno."}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {visibleDrones.map((a) => {
              const sel = (form.drones_bookings || []).some((b) => b.artist_id === a.id);
              return (
                <button key={a.id} type="button" onClick={() => toggleDrone(a.id)} data-testid={`drone-${a.id}`}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${sel ? "border-[#2563EB] bg-[#EFF6FF]" : "border-slate-200 hover:border-[#2563EB]/50 bg-white"}`}>
                  <div className="flex justify-between items-start">
                    <p className="font-ui font-semibold text-[#0F2741]">{a.name}</p>
                    <span className="text-sm font-bold text-[#1D4ED8]">€{a.price_eur}</span>
                  </div>
                  {(a.city || a.region) && <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[a.city, a.region].filter(Boolean).join(" · ")}</p>}
                </button>
              );
            })}
          </div>
        )}
        </div>)}
      </section>

      {/* Artists */}
      <section data-testid="services-artists" className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <button type="button" onClick={() => toggleSec("artists")} data-testid="accordion-header-artists"
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition">
          <Music2 className="w-5 h-5 text-[#2563EB]" />
          <h3 className="font-ui font-semibold text-[#0F2741]">Artisti per lo spettacolo</h3>
          {(form.artists_bookings || []).length > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{(form.artists_bookings || []).length} aggiunti</span>
          )}
          <ChevronDown className={`w-5 h-5 ml-auto text-slate-400 transition-transform ${openSecs.artists ? "rotate-180" : ""}`} />
        </button>
        {openSecs.artists && (<div className="p-4 border-t border-slate-100">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs text-slate-500">{favStage.length} preferiti · {stageArtists.length} totali</span>
          <button type="button" onClick={() => setStageShowAll((v) => !v)} data-testid="btn-toggle-stage-all"
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${stageShowAll ? "bg-[#EFF6FF] text-[#1D4ED8] border-[#2563EB]/30" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            <Star className={`w-3 h-3 ${stageShowAll ? "" : "fill-amber-400 text-amber-500"}`} />
            {stageShowAll ? "Solo preferiti" : `Tutto il DB (${stageArtists.length})`}
          </button>
          <button type="button" onClick={addBooking} data-testid="btn-add-artist-booking" className="btn-primary ml-auto">
            <Plus className="w-4 h-4" /> Aggiungi artista
          </button>
        </div>
        {stageShowAll && (
          <div className="mb-3 p-3 rounded-lg border border-slate-200 bg-slate-50 flex flex-wrap gap-2">
            <Filter className="w-4 h-4 text-slate-400 self-center" />
            <select value={sFilter.category} onChange={(e) => setSFilter({ ...sFilter, category: e.target.value })} data-testid="stage-filter-category" className="px-2 py-1.5 rounded border border-slate-200 text-xs bg-white">
              <option value="all">Tutte tipologie</option>
              {Object.entries(CAT_LABELS).filter(([k]) => !["fotografo", "dronista"].includes(k)).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
            <select value={sFilter.region} onChange={(e) => setSFilter({ ...sFilter, region: e.target.value })} data-testid="stage-filter-region" className="px-2 py-1.5 rounded border border-slate-200 text-xs bg-white">
              <option value="all">Tutte regioni</option>
              {ITALIAN_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input value={sFilter.city} onChange={(e) => setSFilter({ ...sFilter, city: e.target.value })} placeholder="Città" data-testid="stage-filter-city" className="px-2 py-1.5 rounded border border-slate-200 text-xs" />
            <input type="number" min="0" step="100" value={sFilter.max} onChange={(e) => setSFilter({ ...sFilter, max: e.target.value })} placeholder="€ max" data-testid="stage-filter-max" className="px-2 py-1.5 rounded border border-slate-200 text-xs w-24" />
            <span className="text-xs text-slate-500 self-center ml-auto">{stagePool.length} risultati</span>
          </div>
        )}
        {stagePool.length === 0 && (form.artists_bookings || []).length === 0 && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 p-4 rounded-lg mb-3">
            Non hai ancora salvato artisti preferiti. Clicca "Tutto il DB" qui sopra, oppure vai nella pagina Artisti per scegliere i tuoi preferiti.
          </div>
        )}

        {/* Booking panels (selected artists) — shown ABOVE the browse grid */}
        {(form.artists_bookings || []).length > 0 && (
          <div className="space-y-3 mb-4">
            {(form.artists_bookings || []).map((b, idx) => {
              const a = artists.find((x) => x.id === b.artist_id);
              if (a && b.artist_id) {
                return (
                  <BookingPanel
                    key={idx}
                    title={`Artista ${idx + 1} selezionato`}
                    entity={a}
                    booking={b}
                    onChange={(patch) => updateBooking(idx, patch)}
                    onRemove={() => removeBooking(idx)}
                    testidPrefix={`artist-booking-${idx}`}
                    icon={Music2}
                  />
                );
              }
              return (
                <div key={idx} className="p-4 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/40" data-testid={`artist-booking-empty-${idx}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-11">
                      <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Seleziona un artista dal database</label>
                      <select value={b.artist_id} onChange={(e) => {
                        const sel = artists.find((x) => x.id === e.target.value);
                        updateBooking(idx, { artist_id: e.target.value, custom_price: sel?.price_eur ?? null });
                      }} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
                        <option value="">— Seleziona artista —</option>
                        {Object.entries(grouped).map(([cat, list]) => (
                          <optgroup key={cat} label={CAT_LABELS[cat] || cat.toUpperCase()}>
                            {list.map((x) => <option key={x.id} value={x.id}>{x.name} · €{x.price_eur}</option>)}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-1 text-right">
                      <button type="button" onClick={() => removeBooking(idx)} className="text-rose-400 hover:text-rose-600 p-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Browse artist cards (auto-add booking on click) — stesso stile fotografi/droni */}
        {stagePool.length === 0 ? (
          <p className="text-sm text-slate-500">
            {stageShowAll ? "Nessun artista disponibile con questi filtri." : "Nessun artista nei preferiti. Espandi il DB per sceglierne uno."}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {stagePool.map((a) => {
              const sel = (form.artists_bookings || []).some((b) => b.artist_id === a.id);
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => addBookingFor(a)}
                  disabled={sel}
                  data-testid={`artist-card-${a.id}`}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${sel ? "border-emerald-300 bg-emerald-50/40 opacity-70 cursor-not-allowed" : "border-slate-200 hover:border-[#2563EB]/50 bg-white"}`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <p className="font-ui font-semibold text-[#0F2741]">{a.name}</p>
                    <span className="text-sm font-bold text-[#1D4ED8] shrink-0">€{a.price_eur}</span>
                  </div>
                  <p className="text-[10px] uppercase tracking-wider text-[#1D4ED8] mt-0.5">{CAT_LABELS[a.category] || a.category}</p>
                  {a.specialty && <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{a.specialty}</p>}
                  {(a.city || a.region) && <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[a.city, a.region].filter(Boolean).join(" · ")}</p>}
                  {sel && <p className="text-[10px] text-emerald-700 mt-1 font-semibold">✓ Già aggiunto</p>}
                </button>
              );
            })}
          </div>
        )}

        {(form.artists_bookings || []).length === 0 && stagePool.length === 0 && (
          <div className="text-sm text-slate-500 p-6 rounded-xl border border-dashed border-slate-300 text-center mt-3">
            Nessun artista selezionato. Clicca "Aggiungi artista" o usa i preferiti.
          </div>
        )}
        </div>)}
      </section>

      {/* Cake */}
      <section data-testid="services-cake" className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <button type="button" onClick={() => toggleSec("cake")} data-testid="accordion-header-cake"
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition">
          <Cake className="w-5 h-5 text-[#2563EB]" />
          <h3 className="font-ui font-semibold text-[#0F2741]">Torta</h3>
          {form.cake?.enabled && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{form.cake.tier?.toUpperCase()}</span>
          )}
          <ChevronDown className={`w-5 h-5 ml-auto text-slate-400 transition-transform ${openSecs.cake ? "rotate-180" : ""}`} />
        </button>
        {openSecs.cake && (<div className="p-4 border-t border-slate-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Aggiungi una torta personalizzata</span>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" data-testid="toggle-cake" checked={!!form.cake?.enabled} onChange={(e) => setForm({ ...form, cake: { ...form.cake, enabled: e.target.checked } })} className="accent-[#2563EB]" />
            Inserisci torta nell'evento
          </label>
        </div>

        {form.cake?.enabled && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { key: "base", label: "Base", desc: "Pan di Spagna, crema chantilly, fragole" },
                { key: "superior", label: "Superior", desc: "A piani con marzapane, decorazioni floreali" },
                { key: "luxury", label: "Luxury", desc: "Multi-piano, sculture in zucchero, oro alimentare" },
              ].map((t) => {
                const sel = form.cake.tier === t.key;
                return (
                  <button key={t.key} type="button" onClick={() => setCakeTier(t.key)} data-testid={`cake-tier-${t.key}`}
                    className={`text-left p-5 rounded-xl border-2 transition-all ${sel ? "border-[#2563EB] bg-gradient-to-br from-[#EFF6FF] to-white" : "border-slate-200 hover:border-[#2563EB]/50 bg-white"}`}>
                    <div className="flex justify-between items-start">
                      <p className="font-display text-xl text-[#0F2741]">{t.label}</p>
                      <span className="font-display text-2xl text-[#2563EB]">€{cakePrices[t.key]}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">{t.desc}</p>
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Per quante persone</label>
                <input type="number" min="0" value={form.cake.persons || ""} data-testid="input-cake-persons" onChange={(e) => setForm({ ...form, cake: { ...form.cake, persons: parseInt(e.target.value) || 0 } })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Prezzo torta € (editabile)</label>
                <input type="number" step="10" value={form.cake.price || 0} data-testid="input-cake-price" onChange={(e) => setForm({ ...form, cake: { ...form.cake, price: parseFloat(e.target.value) || 0 } })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 font-semibold" />
              </div>
            </div>
            <p className="text-xs text-slate-400">I prezzi default delle tre tipologie si configurano nelle Impostazioni.</p>
          </div>
        )}
        </div>)}
      </section>
    </div>
  );
}

function StepMenu({ form, setForm, dishes }) {
  const cats = ["antipasto", "primo", "secondo", "contorno", "dolce", "pizza"];
  const labels = { antipasto: "Antipasti", primo: "Primi", secondo: "Secondi", contorno: "Contorni", dolce: "Dolci", pizza: "Pizze" };

  const toggle = (cat, id) => {
    const sel = form.dishes[cat] || [];
    const next = sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id];
    setForm({ ...form, dishes: { ...form.dishes, [cat]: next } });
  };

  return (
    <div>
      <h2 className="font-display text-2xl text-[#0F2741] mb-2">Componi il menù</h2>
      <p className="text-slate-500 mb-8">Seleziona uno o più piatti per categoria. {Object.values(form.dishes).flat().length} piatti scelti.</p>

      <div className="space-y-6">
        {cats.map((cat) => {
          const list = dishes.filter((d) => d.category === cat);
          return (
            <div key={cat}>
              <h3 className="font-ui font-semibold text-[#0F2741] mb-3 flex items-center gap-2">
                {labels[cat]}
                <span className="text-xs font-normal text-slate-400">({(form.dishes[cat] || []).length}/{list.length})</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-80 overflow-y-auto pr-1">
                {list.map((d) => {
                  const selected = (form.dishes[cat] || []).includes(d.id);
                  return (
                    <button key={d.id} type="button" onClick={() => toggle(cat, d.id)} data-testid={`dish-${d.id}`}
                      className={`text-left p-3 rounded-lg border transition-all ${selected ? "border-[#2563EB] bg-[#EFF6FF]" : "border-slate-200 hover:border-[#2563EB]/50 bg-white"}`}>
                      <div className="flex justify-between items-start">
                        <p className="font-medium text-sm text-[#0F2741]">{d.name}</p>
                        <span className="text-xs font-semibold text-[#1D4ED8]">€{d.cost_eur}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-1">{d.ingredients?.join(", ")}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StepDrinks({ form, setForm }) {
  const update = (key, patch) => {
    const cur = form.drinks_config?.[key] || { mode: "none", qty: 1, per_people: 4, price: 5 };
    setForm({ ...form, drinks_config: { ...form.drinks_config, [key]: { ...cur, ...patch } } });
  };

  const modeStyle = (mode) => ({
    included: "bg-emerald-500 text-white border-emerald-500",
    paid: "bg-amber-500 text-white border-amber-500",
    none: "bg-slate-200 text-slate-500 border-slate-200",
  })[mode] || "";

  return (
    <div>
      <h2 className="font-display text-2xl text-[#0F2741] mb-2">Bevande</h2>
      <p className="text-slate-500 mb-8">Per ogni categoria scegli: <strong>Compreso</strong> (incluso nel prezzo, X bottiglie ogni Y persone), <strong>A pagamento</strong> (non incluso, fatturato a consumo) oppure <strong>Non richiesto</strong>.</p>

      <div className="space-y-3">
        {DRINK_CATEGORIES.map((d) => {
          const cfg = form.drinks_config?.[d.key] || { mode: "none", qty: 1, per_people: d.per, price: d.price };
          const guests = Number(form.guests || 0);
          const bottles = cfg.mode === "included" ? Math.ceil(guests / Math.max(1, cfg.per_people)) * Math.max(1, cfg.qty) : 0;
          const total = bottles * (cfg.price || 0);

          return (
            <div key={d.key} className={`rounded-xl border transition-all ${cfg.mode === "included" ? "border-[#2563EB] bg-[#EFF6FF]" : cfg.mode === "paid" ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
              <div className="p-4 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-3 min-w-[180px]">
                  <span className="text-2xl">{d.icon}</span>
                  <div>
                    <p className="font-ui font-semibold text-[#0F2741]">{d.label}</p>
                    <p className="text-xs text-slate-500">€{d.price.toFixed(2)} default</p>
                  </div>
                </div>

                <div className="flex gap-1 ml-auto" data-testid={`drink-mode-${d.key}`}>
                  {[
                    { key: "included", label: "Compreso" },
                    { key: "paid", label: "A pagamento" },
                    { key: "none", label: "Non richiesto" },
                  ].map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => update(d.key, { mode: m.key })}
                      data-testid={`drink-${d.key}-${m.key}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${cfg.mode === m.key ? modeStyle(m.key) : "bg-white text-slate-500 border-slate-200 hover:border-[#2563EB]"}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {cfg.mode === "included" && (
                <div className="px-4 pb-4 pt-2 border-t border-[#2563EB]/20 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Quantità</label>
                    <input type="number" min="1" value={cfg.qty} onChange={(e) => update(d.key, { qty: parseInt(e.target.value) || 1 })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">ogni N persone</label>
                    <input type="number" min="1" value={cfg.per_people} onChange={(e) => update(d.key, { per_people: parseInt(e.target.value) || 1 })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">€ a unità</label>
                    <input type="number" step="0.10" value={cfg.price} onChange={(e) => update(d.key, { price: parseFloat(e.target.value) || 0 })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Stima evento</p>
                    <p className="font-display text-2xl text-[#0F2741]">{bottles} <span className="text-xs text-slate-400 normal-case">unità</span></p>
                    <p className="text-xs text-[#2563EB] font-semibold">€ {total.toFixed(2)}</p>
                  </div>
                </div>
              )}

              {cfg.mode === "paid" && (
                <div className="px-4 pb-4 pt-2 border-t border-amber-200">
                  <p className="text-xs text-amber-700">Le bevande di questa categoria saranno servite ma <strong>non incluse</strong> nel preventivo. Verranno fatturate a parte secondo il consumo del cliente.</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StepConfirm({ form, dishes, artists }) {
  const dishCount = Object.values(form.dishes).flat().length;
  const bookingIds = (form.artists_bookings || []).map((b) => b.artist_id).filter(Boolean);
  const legacyIds = form.timeline.filter((t) => !t.skip && t.artist_id).map((t) => t.artist_id);
  const chosenArtists = artists.filter((a) => [...bookingIds, ...legacyIds].includes(a.id));
  const photographerIds = (form.photographers_bookings || []).map((b) => b.artist_id);
  const droneIds = (form.drones_bookings || []).map((b) => b.artist_id);
  const photographers = artists.filter((a) => photographerIds.includes(a.id));
  const drones = artists.filter((a) => droneIds.includes(a.id));
  const drinksIncluded = DRINK_CATEGORIES.filter((d) => form.drinks_config?.[d.key]?.mode === "included");
  const drinksPaid = DRINK_CATEGORIES.filter((d) => form.drinks_config?.[d.key]?.mode === "paid");

  return (
    <div>
      <h2 className="font-display text-2xl text-[#0F2741] mb-2">Riepilogo evento</h2>
      <p className="text-slate-500 mb-8">Verifica e conferma. Calcoleremo costi, ordini ai fornitori e staff.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SummaryCard label="Tipo" value={form.event_type} />
        <SummaryCard label="Cliente" value={form.client_name || "—"} />
        <SummaryCard label="Ospiti" value={`${form.guests} persone`} />
        <SummaryCard label="Data" value={`${form.event_date || "—"} · ${form.start_time}-${form.end_time}`} />
        <SummaryCard label="Location" value={form.location?.name ? `${form.location.name}${form.location.external && form.location.price ? ` · €${form.location.price}` : ""}` : "—"} />
        <SummaryCard label="Addobbi" value={form.decorations?.enabled ? `${form.decorations.items.length} voci · €${form.decorations.items.reduce((s, it) => s + (it.qty || 1) * (it.price || 0), 0).toFixed(0)}` : "Non previsti"} />
        <SummaryCard label="Piatti selezionati" value={`${dishCount} piatti`} />
        <SummaryCard label="Steps programma" value={`${form.timeline.filter(t => !t.skip).length} attivi su ${form.timeline.length}`} />
        <SummaryCard label={`Fotografi (${photographers.length})`} value={photographers.length ? photographers.map((p) => p.name).join(", ") : "—"} />
        <SummaryCard label={`Droni (${drones.length})`} value={drones.length ? drones.map((p) => p.name).join(", ") : "—"} />
        <SummaryCard label="Artisti" value={chosenArtists.length ? chosenArtists.map(a => a.name).join(", ") : "Nessuno"} />
        <SummaryCard label="Torta" value={form.cake?.enabled ? `${form.cake.tier?.toUpperCase()} per ${form.cake.persons || form.guests} pers · €${form.cake.price}` : "Non prevista"} />
        <SummaryCard label="Bevande incluse" value={drinksIncluded.length ? drinksIncluded.map((d) => d.label).join(", ") : "Nessuna"} />
        <SummaryCard label="Bevande a pagamento" value={drinksPaid.length ? drinksPaid.map((d) => d.label).join(", ") : "Nessuna"} />
      </div>
    </div>
  );
}
function SummaryCard({ label, value }) {
  return (
    <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className="font-ui font-semibold text-[#0F2741] mt-1 capitalize">{value}</p>
    </div>
  );
}


function BookingPanel({ title, entity, booking, onChange, onRemove, testidPrefix, icon: Icon }) {
  if (!entity) return null;
  const basePrice = entity.price_eur || 0;
  const currentPrice = booking.custom_price !== undefined && booking.custom_price !== "" ? booking.custom_price : basePrice;
  return (
    <div className="mb-3 p-4 rounded-xl border-2 border-[#2563EB] bg-[#EFF6FF]" data-testid={testidPrefix}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#0F2741] grid place-items-center text-white">
            {Icon ? <Icon className="w-5 h-5" /> : null}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#1D4ED8] font-bold">{title}</p>
            <p className="font-ui font-bold text-[#0F2741] text-lg">{entity.name}</p>
            {entity.specialty && <p className="text-xs text-[#1D4ED8]">{entity.specialty}</p>}
            {(entity.city || entity.region) && <p className="text-xs text-slate-500 mt-0.5">{[entity.city, entity.region].filter(Boolean).join(" · ")}</p>}
          </div>
        </div>
        <button type="button" onClick={onRemove} data-testid={`${testidPrefix}-remove`} className="text-rose-500 hover:bg-white rounded-lg p-2 transition" title="Rimuovi">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ora di arrivo prevista</span>
          <input
            type="time"
            value={booking.arrival_time || ""}
            onChange={(e) => onChange({ arrival_time: e.target.value })}
            data-testid={`${testidPrefix}-arrival`}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ore di permanenza</span>
          <input
            type="number"
            min="0"
            step="0.5"
            value={booking.hours ?? ""}
            placeholder="es: 4"
            onChange={(e) => onChange({ hours: e.target.value })}
            data-testid={`${testidPrefix}-hours`}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Compenso € (editabile)</span>
          <input
            type="number"
            min="0"
            step="10"
            value={currentPrice ?? ""}
            placeholder={String(basePrice)}
            onChange={(e) => onChange({ custom_price: e.target.value === "" ? "" : parseFloat(e.target.value) })}
            data-testid={`${testidPrefix}-price`}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white font-semibold"
          />
        </label>
      </div>
      <p className="text-xs text-slate-500 mt-2">
        Prezzo medio listino: <strong>€{basePrice}</strong>.
        {currentPrice !== basePrice && currentPrice !== "" && (
          <span className="text-amber-700"> Modificato a €{currentPrice}</span>
        )}
      </p>
    </div>
  );
}
